import type { ClientCompanyUpdateDto } from '@/types/company'
import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'preact/hooks'
import { useLookups } from '@/hooks/useLookups'
import { CompanyLookup } from '@/components/common/CompanyLookup'
import { SlugInput } from '@/components/common/SlugInput'
import { Building2, Edit3 } from 'lucide-preact'

interface CompanyEditFormProps {
  company: ClientCompanyUpdateDto
  onSave: (company: ClientCompanyUpdateDto) => void
}

export function CompanyEditForm({ company, onSave }: CompanyEditFormProps) {
  const { getStatusOptions } = useLookups()
  const [companyName, setCompanyName] = useState(company.Name || '')
  const [enrichLoading, setEnrichLoading] = useState(false)
  const [isChangingCompany, setIsChangingCompany] = useState(false)

  const LIMITS = {
    NAME_MAX: 200,
    URL_MAX: 2083,
  }

  const isValidHttpUrl = (value: string) => {
    try {
      const url = new URL(value)
      return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.')
    } catch {
      return false
    }
  }

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<ClientCompanyUpdateDto>({
    mode: 'onChange',
    defaultValues: company
  })

  // Reset when the incoming company prop changes (e.g., open/close)
  useEffect(() => {
    reset(company)
    setCompanyName(company.Name || '')
    setIsChangingCompany(false)
    // Explicitly set CompanyId to ensure it's in the form state
    setValue('CompanyId', company.CompanyId || '', { shouldValidate: false })
  }, [company, reset, setValue])

  // Register CompanyId validation
  useEffect(() => {
    register('CompanyId', {
      required: 'Company is required. Please search for a company or enter a LinkedIn Company ID.',
      validate: v => {
        if (!v || v.trim().length === 0) {
          return 'Company is required. Please search for a company or enter a LinkedIn Company ID.'
        }
        return true
      }
    })
  }, [register])

  return (
    <form id="company-edit-form" onSubmit={handleSubmit((vals) => onSave(vals))} className="space-y-6">
      {/* Hidden input to ensure CompanyId is always included in form submission */}
      <input type="hidden" value={watch('CompanyId') || ''} {...register('CompanyId')} />

      <div className="grid grid-cols-1 gap-6">
        <div className="space-y-4">
          {/* Show Company Lookup only if changing or no company selected yet */}
          {isChangingCompany ? (
            <div>
              <CompanyLookup
                companyId={watch('CompanyId')}
                companyName={companyName}
                onChange={(id, name) => {
                  setValue('CompanyId', id, { shouldValidate: true, shouldDirty: true })
                  setValue('Name', name, { shouldValidate: true, shouldDirty: true })
                  setCompanyName(name)
                }}
                onLoadingChange={setEnrichLoading}
                error={errors.CompanyId?.message as string}
                disabled={enrichLoading}
                required={true}
              />
              <button
                type="button"
                onClick={() => setIsChangingCompany(false)}
                className="mt-2 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 underline"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="p-3 rounded-md bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Building2 className="w-5 h-5 text-slate-500 dark:text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Company</div>
                    {watch('CompanyId') ? (
                      <a
                        href={`https://www.linkedin.com/company/${watch('CompanyId')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline truncate block"
                        onClick={e => e.stopPropagation()}
                      >
                        {companyName || watch('CompanyId')} →
                      </a>
                    ) : (
                      <div className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                        {companyName || 'No company selected'}
                      </div>
                    )}
                    {watch('CompanyId') && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        LinkedIn ID: {watch('CompanyId')}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsChangingCompany(true)}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  Change Company
                </button>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Company Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              {...register('Name', {
                required: 'Company name is required',
                maxLength: { value: LIMITS.NAME_MAX, message: `Company name must be ${LIMITS.NAME_MAX} characters or less` },
                validate: v => !!(v || '').trim() || 'Name cannot be just whitespace'
              })}
              className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 ${errors.Name ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500 dark:focus:ring-primary-400'}`}
              placeholder="Enter company name"
            />
            {errors.Name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.Name.message as string}</p>}
          </div>
          <SlugInput
            label="Slug"
            required
            value={(watch('Slug') as string) || ''}
            onChange={() => { /* slug is immutable once set; SlugInput is rendered locked */ }}
            locked
            lockedMessage="Slug is set at creation and cannot be changed."
            helperText="This is the subdomain for this company's deployed landing pages."
          />
          <input type="hidden" {...register('Slug')} />
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Website <span className="text-red-500">*</span></label>
            <input
              type="url"
              {...register('CompanyUrl', {
                required: 'Website is required',
                maxLength: { value: LIMITS.URL_MAX, message: 'Website URL too long' },
                validate: value => isValidHttpUrl(value) || 'Enter a valid http(s) URL with a domain'
              })}
              className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 ${errors.CompanyUrl ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500 dark:focus:ring-primary-400'}`}
              placeholder="https://example.com"
            />
            {errors.CompanyUrl && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.CompanyUrl.message as string}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Company Type <span className="text-red-500">*</span></label>
              <select
                {...register('CompanyType', { required: 'Company type is required' })}
                className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 ${errors.CompanyType ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500 dark:focus:ring-primary-400'}`}
              >
                <option value="">Select company type</option>
                <option value="Internal">Internal</option>
                <option value="Customer">Customer</option>
                <option value="Partner">Partner</option>
              </select>
              {errors.CompanyType && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.CompanyType.message as string}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Status <span className="text-red-500">*</span></label>
              <select
                {...register('Status', { required: 'Status is required' })}
                className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 ${errors.Status ? 'border-red-300 dark:border-red-600 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600 focus:ring-primary-500 dark:focus:ring-primary-400'}`}
              >
                <option value="">Select status…</option>
                {getStatusOptions().map(opt => (
                  <option key={opt.Key} value={opt.Key}>{opt.Description}</option>
                ))}
              </select>
              {errors.Status && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.Status.message as string}</p>}
            </div>
          </div>
        </div>
      </div>
    </form>
  )
}
